import { FC, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, Skeleton } from '@ibsys/kit';
import { isThresholdApplied, useStrategistHierarchy } from '@/entities/strategist';
import type { StrategistHierarchyCluster, StrategistHierarchyRequest } from '@/entities/strategist';
import { ConceptCardDrawer, useOpenConceptCard } from '@/features/concept-card';
import { DialogsDrawerContent, useOpenDialogsDrawer } from '@/features/dialogs-drawer';
import { PageContainer } from '@/shared/ui';
import { StrategistFilters } from '@/widgets/StrategistFilters';
import { useHierarchySettings } from '../model/useHierarchySettings';
import { HierarchyControls } from './HierarchyControls';
import { HierarchyKpi } from './HierarchyKpi';
import { HierarchyTable } from './HierarchyTable';

/**
 * Страница «Иерархия понятий» раздела «Стратег»: концепты выбранной категории
 * (роль: возражение / аргумент / вопрос), кластеризованные в иерархию по порогу
 * близости. Держит сквозной фильтр, целевую метрику, категорию и порог; на каждом
 * срезе отдаёт таблицу кластеров с медоидом, числом понятий, диалогов, долей
 * целевого исхода (вероятность выхода на успех) с влиянием и примерами формулировок.
 * Целевая метрика по умолчанию не выбрана — тогда доля цели и влияние не показываются.
 * Весь срез (фильтр, метрика, категория, порог) запоминается за пользователем в настройках
 * интерфейса (см. {@link useHierarchySettings}) и восстанавливается при следующем открытии.
 * Категория и порог сверяются с доступными значениями из ответа: несохранённые или уже
 * недоступные берутся из него — категория первая доступная, порог средний из набора.
 */
export const StrategistHierarchyPage: FC = () => {
  const { t } = useTranslation(['common', 'strategist']);

  // Состояние, влияющее на запрос: живёт в настройках пользователя и восстанавливается
  // при следующем открытии страницы.
  const {
    filterValue,
    target,
    category,
    threshold,
    handleChangeFilters,
    handleChangeTarget,
    handleChangeCategory,
    handleChangeThreshold,
    handleApplyAvailable,
  } = useHierarchySettings();

  const body: StrategistHierarchyRequest = useMemo(
    () => ({
      filters: filterValue.filters,
      dateFrom: filterValue.dateFrom,
      dateTo: filterValue.dateTo,
      // Метрика не выбрана — `targetAttribute` в тело не уходит вовсе.
      ...(target ? { targetAttribute: target } : {}),
      category,
      threshold,
    }),
    [filterValue, target, category, threshold],
  );

  const { data, isLoading, isPlaceholderData } = useStrategistHierarchy(body);

  // Категория ответа должна совпадать с выбранной, иначе в таблице оказались бы кластеры
  // прошлого среза. Пока категория не подставлена (первый ответ ещё не пришёл), сверять нечего.
  const isCategoryApplied = category === undefined || data?.category === category;

  // Показывать можно только данные, посчитанные теми же параметрами, что уйдут в срез строки:
  // пока идёт запрос за новыми или порог ещё не подставлен, таблица и панель диалогов разойдутся.
  const isAwaitingData =
    isLoading || isPlaceholderData || !isCategoryApplied || !isThresholdApplied(data?.thresholds, [threshold]);

  // Доступные категории и пороги известны только из ответа: по нему же сверяются
  // сохранённые за пользователем значения, а не прошедшие сверку заменяются значениями
  // из ответа (категория — первая доступная, порог — средний из набора).
  useEffect(() => {
    if (!data) return;
    handleApplyAvailable(data.categories, data.thresholds);
  }, [data, handleApplyAvailable]);

  const clusters = data?.clusters ?? [];

  // Без выбранной метрики доля цели и влияние бессмысленны: бэк молча считает целью
  // удержание, поэтому эти показатели просто не показываются.
  const isTargetSelected = target !== undefined;

  const handleConceptOpen = useOpenConceptCard(filterValue);

  const openDialogs = useOpenDialogsDrawer({ ...filterValue, target });
  // Строка проваливает в свои диалоги: понятие раскрывается обратно в кластер на текущем
  // пороге, поэтому показатели диалогов совпадают с показателями строки.
  const handleRowOpen = useCallback(
    (cluster: StrategistHierarchyCluster) => {
      openDialogs({
        conceptId: cluster.medoidConceptId,
        conceptThreshold: data?.threshold,
        title: cluster.label,
      });
    },
    [openDialogs, data?.threshold],
  );

  return (
    <PageContainer className="flex flex-col gap-5">
      <h1 className="text-xl font-semibold text-foreground">{t('common:routes.strategistHierarchy')}</h1>

      <StrategistFilters value={filterValue} onChange={handleChangeFilters} />

      <HierarchyControls
        target={target}
        onTargetChange={handleChangeTarget}
        categories={data?.categories ?? []}
        category={category}
        onCategoryChange={handleChangeCategory}
        thresholds={data?.thresholds ?? []}
        threshold={threshold}
        onThresholdChange={handleChangeThreshold}
      />

      {isAwaitingData ? (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </div>
          <Skeleton className="h-96 w-full" />
        </div>
      ) : clusters.length === 0 ? (
        <Card>
          <CardContent className="p-10 flex items-center justify-center">
            <p className="text-sm text-muted-foreground">{t('common:emptyTable')}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-5">
          <HierarchyKpi
            totalDialogs={data?.totalDialogs ?? 0}
            baselineRate={data?.baselineRate ?? null}
            isTargetSelected={isTargetSelected}
            threshold={data?.threshold ?? 0}
            clusters={clusters.length}
          />
          <HierarchyTable
            clusters={clusters}
            totalDialogs={data?.totalDialogs ?? 0}
            isTargetSelected={isTargetSelected}
            onRowOpen={handleRowOpen}
            onConceptOpen={handleConceptOpen}
          />
        </div>
      )}
      <ConceptCardDrawer />
      <DialogsDrawerContent />
    </PageContainer>
  );
};
